import {
  ClockIcon,
  EyeIcon,
  EyeSlashIcon,
  HouseIcon,
  LightningIcon,
  PlusCircleIcon,
  PlusIcon,
} from '@phosphor-icons/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { ADMIN_OR_PM } from '@/components/Can'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { NavbarItem } from '@/components/layout/page-header/page-header.types'
import ReservationsAddTimeBlockModal from '@/components/Reservations/ReservationsAddTimeBlockModal/ReservationsAddTimeBlockModal'
import { Button } from '@/components/ui/button'
import { DropdownMenu } from '@/components/ui/dropdown-menu'
import { Icon } from '@/components/ui/icon'
import { NavbarLink, NavbarMenu } from '@/components/ui/navbar-menu'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { popupFactory } from '@/factories'
import { useCaptureEvent } from '@/hooks'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { selectUserRole } from '@/reducers/userDataReducer'

import AmenityMarketplaceModal from '../../components/Reservations/AmenityMarketplaceModal/AmenityMarketplaceModal'
import { LEGACY_RESERVATIONS_BOOKINGS_PATH } from './reservations.constants'
import * as S from './reservations.page.styled'
import { ReservationsAnalytics } from './reservations-analytics'
import { AddReservationModal } from './reservations-dashboard/add-reservation-modal'

const LEGACY_AMENITIES_PATH = '/dashboard/building-dashboard/reservations/amenities'
const LEGACY_CREATE_AMENITY_PATH = '/dashboard/building-dashboard/reservations/amenities/create'
const TIME_BLOCKS_PATH = '/reservations/time-blocks'
const AMENITIES_PATH = '/reservations/amenities'

export const ReservationsPage = () => {
  const { t } = useTranslation(['reservations'])
  const navigate = useNavigate()
  const location = useLocation()
  const { captureEvent } = useCaptureEvent()
  const activeBuilding = useSelector(selectActiveBuilding)
  // Note: intentionally plain component state, not useLocalStorage - per spec, analytics
  // always starts closed on page open and its state is not remembered across visits.
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false)

  const userRole = useSelector(selectUserRole)
  const canSeeAmenities = !!userRole && ADMIN_OR_PM.includes(userRole)

  const isAmenitiesTab = location.pathname.startsWith(AMENITIES_PATH)

  const handleUIToggle = () => {
    const legacyPath = isAmenitiesTab ? LEGACY_AMENITIES_PATH : LEGACY_RESERVATIONS_BOOKINGS_PATH
    captureEvent('user_action', {
      from_path: location.pathname,
      name: 'design_toggle',
      screen: 'Reservations',
      to_path: legacyPath,
    })
    navigate(legacyPath)
  }

  const handleGenerateIncome = () => {
    // Note: selectedBuilding is injected by AmenityMarketplaceModal's own connect() mapStateToProps.
    popupFactory.open(AmenityMarketplaceModal, {
      isOpen: true,
    })
  }

  const handleAddReservation = () => {
    popupFactory.open(AddReservationModal, {})
  }

  const handleAddTimeBlock = () => {
    popupFactory.open(
      ReservationsAddTimeBlockModal as unknown as Parameters<typeof popupFactory.open>[0],
      { buildingData: activeBuilding },
    )
  }

  const handleAddAmenity = () => {
    navigate(LEGACY_CREATE_AMENITY_PATH)
  }

  const navbarItems: NavbarItem[] = [
    { label: t('reservations:page.tabs.reservations'), to: { path: '/reservations/bookings' } },
    { label: t('reservations:page.tabs.timeBlocks'), to: { path: TIME_BLOCKS_PATH } },
    {
      hidden: !canSeeAmenities,
      label: t('reservations:page.tabs.amenities'),
      to: { path: AMENITIES_PATH },
    },
  ]
  const visibleNavbarItems = navbarItems.filter((item) => !item.hidden)

  return (
    <S.ScrollableDashboardContainer className="ctp-reservations__wrapper">
      <Page>
        <S.ReservationsPageHeader>
          <PageHeader
            title={t('reservations:page.title')}
            titleExtra={
              <S.HeaderAnalyticsSlot>
                <Button
                  size="small"
                  use="secondary"
                  onClick={() => setIsAnalyticsOpen(!isAnalyticsOpen)}
                >
                  <Icon icon={isAnalyticsOpen ? EyeSlashIcon : EyeIcon} />
                  {isAnalyticsOpen
                    ? t('reservations:page.analyticsToggle.hide')
                    : t('reservations:page.analyticsToggle.show')}
                </Button>
              </S.HeaderAnalyticsSlot>
            }
            actions={
              <>
                <S.HeaderToggleSlot>
                  <ToggleSwitch
                    falsyLabel={t('reservations:page.designToggle.old')}
                    truthyLabel={t('reservations:page.designToggle.new')}
                    checked={true}
                    onChange={handleUIToggle}
                  />
                </S.HeaderToggleSlot>
                <S.GenerateIncomeButton
                  disabled={!activeBuilding}
                  size="small"
                  use="upsell-secondary"
                  onClick={handleGenerateIncome}
                >
                  <Icon icon={LightningIcon} />
                  {t('reservations:page.actions.generateIncome')}
                </S.GenerateIncomeButton>
                <S.HeaderAddSlot>
                  <DropdownMenu
                    items={[
                      {
                        icon: PlusCircleIcon,
                        label: t('reservations:page.actions.addReservation'),
                        onClick: handleAddReservation,
                      },
                      {
                        icon: ClockIcon,
                        label: t('reservations:page.actions.addTimeBlock'),
                        onClick: handleAddTimeBlock,
                      },
                      {
                        hidden: !canSeeAmenities,
                        icon: HouseIcon,
                        label: t('reservations:page.actions.addAmenity'),
                        onClick: handleAddAmenity,
                      },
                    ]}
                    trigger={() => (
                      <Button disabled={!activeBuilding} size="small" use="primary">
                        {t('reservations:page.actions.add')}
                        <Icon icon={PlusIcon} />
                      </Button>
                    )}
                  />
                </S.HeaderAddSlot>
              </>
            }
          />
        </S.ReservationsPageHeader>
        <S.AnalyticsCollapse in={isAnalyticsOpen}>
          <ReservationsAnalytics isOpen={isAnalyticsOpen} />
        </S.AnalyticsCollapse>
        <NavbarMenu>
          {visibleNavbarItems.map((item) => (
            <NavbarLink key={item.to.path} to={item.to}>
              {item.label}
            </NavbarLink>
          ))}
        </NavbarMenu>
        <Outlet />
      </Page>
    </S.ScrollableDashboardContainer>
  )
}
